import { lazy, Suspense } from 'react';
import { buildWorkspaceSettingsHref } from '@/features/organisations/utils/organisationSettingsRoute';
import { PostOnboardingTutorialDialog } from '@/features/postOnboardingTutorial';
import type { UploadDialogState } from '@/features/executionRuns/hooks/useExecutionRunsPageViewModel.types';
import type { buildExecutionRunsUploadProjectOptions } from '@/features/executionRuns/pages/executionRunsUploadProjectOptions';
import type { usePostOnboardingTutorialState } from '@/features/postOnboardingTutorial';
import type { ReactElement } from 'react';
import type { NavigateFunction } from 'react-router-dom';

const ResultsFileUploadDialog = lazy(async () => {
  const module = await import('@/features/quality/sections/ResultsFileUploadDialog');
  return { default: module.ResultsFileUploadDialog };
});

type ExecutionRunsPageDialogsProps = {
  handleUploadConfirm: () => void;
  navigate: NavigateFunction;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  tutorial: ReturnType<typeof usePostOnboardingTutorialState>;
  uploadDialog: UploadDialogState;
  uploadBlockedReason: string | null;
  uploadProjectOptions: ReturnType<typeof buildExecutionRunsUploadProjectOptions>;
};

const ExecutionRunsPageDialogs = ({
  handleUploadConfirm,
  navigate,
  selectedOrganisationId,
  selectedProjectId,
  tutorial,
  uploadDialog,
  uploadBlockedReason,
  uploadProjectOptions,
}: ExecutionRunsPageDialogsProps): ReactElement => (
  <>
    {uploadDialog.uploadOpen ? (
      <Suspense fallback={null}>
        <ResultsFileUploadDialog
          open={uploadDialog.uploadOpen}
          loading={uploadDialog.uploadMutation.isPending}
          confirmDisabled={uploadDialog.disableUpload}
          onClose={uploadDialog.closeUploadDialog}
          onConfirm={handleUploadConfirm}
          selectedProjectId={selectedProjectId ?? null}
          selectedOrganisationId={selectedOrganisationId}
          uploadProjectId={uploadDialog.uploadProjectId ?? ''}
          uploadTags={uploadDialog.uploadTags}
          uploadTagColors={uploadDialog.uploadTagColors}
          uploadDuplicatePolicy={uploadDialog.uploadDuplicatePolicy}
          uploadRunEstimate={uploadDialog.uploadRunEstimate}
          onUploadProjectIdChange={uploadDialog.setUploadProjectId}
          onUploadTagsChange={uploadDialog.setUploadTags}
          onUploadTagColorsChange={uploadDialog.setUploadTagColors}
          onUploadDuplicatePolicyChange={uploadDialog.setUploadDuplicatePolicy}
          uploadProjectIdFieldId={uploadDialog.uploadProjectIdFieldId}
          projectOptions={uploadProjectOptions}
          uploadFiles={uploadDialog.uploadFiles}
          selectedFiles={uploadDialog.selectedFiles}
          isSupportedFile={uploadDialog.isSupportedFile}
          csvHeaderStatusByName={uploadDialog.csvHeaderStatusByName}
          csvHeaderReasonByName={uploadDialog.csvHeaderReasonByName}
          fileValidationStatusByName={uploadDialog.fileValidationStatusByName}
          fileValidationReasonByName={uploadDialog.fileValidationReasonByName}
          handleFilesSelected={uploadDialog.handleFilesSelected}
          setSelectedFiles={uploadDialog.setSelectedFiles}
          setUploadFiles={uploadDialog.setUploadFiles}
          setUploadError={uploadDialog.setUploadError}
          uploadError={uploadDialog.uploadError}
          setCsvHeaderStatusByName={uploadDialog.setCsvHeaderStatusByName}
          setCsvHeaderReasonByName={uploadDialog.setCsvHeaderReasonByName}
          setFileValidationStatusByName={uploadDialog.setFileValidationStatusByName}
          setFileValidationReasonByName={uploadDialog.setFileValidationReasonByName}
          uploadBlockedReason={uploadBlockedReason}
        />
      </Suspense>
    ) : null}
    <PostOnboardingTutorialDialog
      open={tutorial.isOpen}
      currentStepId={tutorial.currentStepId}
      currentStepIndex={tutorial.currentStepIndex}
      totalSteps={tutorial.totalSteps}
      projectId={selectedProjectId}
      onClose={tutorial.dismissTutorial}
      onSkip={tutorial.dismissTutorial}
      onBack={tutorial.goToPreviousStep}
      onNext={tutorial.goToNextStep}
      onComplete={tutorial.completeTutorial}
      onOpenTeam={() => {
        if (!selectedOrganisationId) return;
        void navigate(buildWorkspaceSettingsHref(selectedOrganisationId));
      }}
    />
  </>
);

export { ExecutionRunsPageDialogs };
